import React, { useMemo, useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { useTable, useFilters } from "react-table";
import fetchAuditData from "../../../utils/fetchAuditData";
import { ColumnFilter } from "./ColumnFilter";
import ReactModal from "react-modal";
import styled from "styled-components";
import { MODAL_STYLES } from "../constants";

const AUDIT_MODAL_STYLES = {
  overlay: MODAL_STYLES.overlay,
  content: {
    ...MODAL_STYLES.content,
    maxWidth: "none",
    width: "90vw",
    maxHeight: "90vh",
    display: "flex",
    flexDirection: "column",
  },
};

const COMMENTS_MODAL_STYLES = {
  overlay: MODAL_STYLES.overlay,
  content: {
    ...MODAL_STYLES.content,
    maxWidth: "480px",
  },
};

const ModalHeader = styled.div`
  background: #CE0E2D;
  color: white;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 700;
  font-size: 1em;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.4em;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  opacity: 0.8;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;

const ModalBody = styled.div`
  padding: 16px 20px 20px;
  overflow: auto;
  flex: 1;
  min-height: 0;
`;

const AuditTable = styled.div`
  table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
    border: 1px solid #e0e0e0;
    font-size: 0.9em;
  }

  thead th {
    background-color: #eee;
    color: var(--clr-grey-2);
    font-weight: 600;
    font-size: 0.92em;
    text-align: left;
    padding: 8px 10px;
    border: 1px solid #e0e0e0;
    white-space: nowrap;
  }

  thead th input {
    width: 90%;
    margin-top: 4px;
    padding: 3px 6px;
    font-size: 0.9em;
    border: 1px solid #ccc;
    border-radius: 3px;
    box-sizing: border-box;
  }

  tbody tr {
    background-color: #f8f8f8;
  }

  tbody tr:hover {
    background-color: #efefef;
  }

  tbody td {
    padding: 8px 10px;
    border: 1px solid #e0e0e0;
    text-align: left;
    vertical-align: top;
    word-wrap: break-word;
    overflow-wrap: break-word;
    white-space: normal;
    color: var(--clr-grey-1);
  }
`;

const FooterBar = styled.div`
  padding: 12px 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
`;

const CloseBtn = styled.button`
  background: white;
  color: #555;
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 6px 20px;
  font-size: 0.85em;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f3f4f6;
    border-color: #999;
    color: #333;
  }
`;

const CommentBody = styled.div`
  padding: 20px 24px;
  text-align: center;
  color: var(--clr-grey-2);
  font-size: 0.95em;
  line-height: 1.6;
  word-wrap: break-word;
`;

const SignOutComments = ({ value }) => {
  const [showComments, setShowComments] = useState(false);

  if (!value) {
    return <span style={{ color: "var(--clr-grey-5)", fontSize: "0.9em" }}>No Comments</span>;
  }

  return (
    <>
      <ReactModal
        isOpen={showComments}
        style={COMMENTS_MODAL_STYLES}
        onRequestClose={() => setShowComments(false)}
        appElement={document.getElementById("root")}
      >
        <ModalHeader>
          Sign Out Comments
          <CloseButton onClick={() => setShowComments(false)}>&times;</CloseButton>
        </ModalHeader>
        <CommentBody>{value}</CommentBody>
        <FooterBar>
          <CloseBtn onClick={() => setShowComments(false)}>Close</CloseBtn>
        </FooterBar>
      </ReactModal>
      <button
        type="button"
        className="vehicle-type-label vehicle-type-button"
        title={value}
        onClick={() => setShowComments(true)}
      >
        See Comments
      </button>
    </>
  );
};

SignOutComments.propTypes = {
  value: PropTypes.string.isRequired,
};

const AuditDetails = ({
  showAuditDetailsModal,
  setShowAuditDetailsModal,
  pulloutDivision,
  assignmentDate,
}) => {
  const [data, setData] = useState([]);
  const [noData, setNoData] = useState(false);
  const getData = useCallback(async () => {
    const response = await fetchAuditData({ pulloutDivision, assignmentDate });
    if (response.success) {
      setData(response.data);
      if (response.data.length === 0) {
        setNoData(true);
      }
    } else {
      console.error(response.message);
    }
  }, [assignmentDate, pulloutDivision]);

  useEffect(() => {
    getData();
  }, [getData]);
  const columns = useMemo(
    () => [
      {
        Header: "Assignment Date",
        accessor: "assignmentDate",
        Filter: ColumnFilter,
        width: "10%",
      },
      {
        Header: "Division",
        accessor: "divisionName",
        Filter: ColumnFilter,
        width: "10%",
      },
      {
        Header: "Message",
        accessor: "auditMessage",
        Filter: ColumnFilter,
        width: "35%",
      },
      {
        Header: "Updated User",
        accessor: "user",
        Filter: ColumnFilter,
        width: "18%",
      },
      {
        Header: "Updated Date",
        accessor: "updatedDate",
        Filter: ColumnFilter,
        width: "15%",
      },
      {
        Header: () => (
          <div>
            SignOut <br /> Comments
          </div>
        ),
        accessor: "signOutComments",
        Filter: ColumnFilter,
        Cell: SignOutComments,
        disableFilters: true,
        width: "12%",
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
    },
    useFilters
  );

  const onClose = () => setShowAuditDetailsModal(false);

  return (
    <ReactModal
      isOpen={showAuditDetailsModal}
      style={AUDIT_MODAL_STYLES}
      onRequestClose={onClose}
      appElement={document.getElementById("root")}
    >
      <ModalHeader>
        Audit Details
        <CloseButton onClick={onClose}>&times;</CloseButton>
      </ModalHeader>
      {noData ? (
        <ModalBody>
          <p style={{ color: "var(--clr-grey-4)", margin: 0 }}>No Audit Details Found</p>
        </ModalBody>
      ) : (
        <ModalBody>
          <AuditTable>
            <table {...getTableProps()}>
              <thead>
                {headerGroups.map((headerGroup, i) => (
                  <tr key={i} {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column, i) => (
                      <th
                        key={i}
                        width={column.width}
                        {...column.getHeaderProps()}
                      >
                        {column.render("Header")}
                        {
                          <div>
                            {column.canFilter
                              ? column.render("Filter")
                              : null}
                          </div>
                        }
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody {...getTableBodyProps()}>
                {rows.map((row, i) => {
                  prepareRow(row);
                  return (
                    <tr
                      key={i}
                      {...row.getRowProps()}
                    >
                      {row.cells.map((cell, i) => {
                        return (
                          <td
                            key={i}
                            {...cell.getCellProps()}
                          >
                            {cell.render("Cell")}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </AuditTable>
        </ModalBody>
      )}
      <FooterBar>
        <CloseBtn onClick={onClose}>Close</CloseBtn>
      </FooterBar>
    </ReactModal>
  );
};

AuditDetails.propTypes = {
  showAuditDetailsModal: PropTypes.bool.isRequired,
  setShowAuditDetailsModal: PropTypes.func.isRequired,
  pulloutDivision: PropTypes.string.isRequired,
  assignmentDate: PropTypes.string.isRequired,
};

export default AuditDetails;
